import { useEffect, useMemo, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { Search, Loader2 } from "lucide-react";
import ProductCard from "@/components/ProductCard";
import { Reveal, Eyebrow } from "@/components/Reveal";
import { fetchProducts } from "@/lib/api";
import { CATEGORIES } from "@/lib/catalog";

export default function Products() {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [category, setCategory] = useState("all");
  const [search, setSearch] = useState("");

  useEffect(() => {
    fetchProducts()
      .then(setProducts)
      .catch(() => setProducts([]))
      .finally(() => setLoading(false));
  }, []);

  const filtered = useMemo(
    () =>
      products.filter(
        (p) =>
          (category === "all" || p.category === category) &&
          p.name.toLowerCase().includes(search.toLowerCase())
      ),
    [products, category, search]
  );

  return (
    <main className="bg-bone min-h-screen" data-testid="products-page">
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-16 pb-24">
        <Reveal>
          <Eyebrow>The Catalog</Eyebrow>
          <h1 className="mt-4 font-serif font-bold text-4xl sm:text-5xl lg:text-6xl tracking-tight text-ink">
            Everything your kitchen
            <span className="italic text-forest"> runs on.</span>
          </h1>
          <p className="mt-5 max-w-xl text-ink-soft">
            Browse our shelves online. Add items to your enquiry list and we will confirm
            availability, pack them fresh, and deliver — no advance payment needed.
          </p>
        </Reveal>

        <Reveal delay={0.1} className="mt-10 flex flex-col lg:flex-row lg:items-center gap-5">
          <div className="relative lg:w-80">
            <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-ink-muted" />
            <input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search atta, dal, ghee…"
              className="w-full rounded-full border border-line bg-white pl-11 pr-4 py-3 text-sm outline-none focus:border-forest focus:ring-2 focus:ring-forest/15"
              data-testid="product-search-input"
            />
          </div>
          <div className="flex flex-wrap gap-2" data-testid="category-tabs">
            {CATEGORIES.map((c) => (
              <button
                key={c.key}
                onClick={() => setCategory(c.key)}
                data-testid={c.testid}
                className={`relative rounded-full px-4 py-2 text-xs font-bold transition-colors duration-300 ${
                  category === c.key ? "text-bone" : "text-ink-soft hover:text-forest bg-white border border-line"
                }`}
              >
                {category === c.key && (
                  <motion.span
                    layoutId="category-pill"
                    className="absolute inset-0 rounded-full bg-forest"
                    transition={{ type: "spring", damping: 26, stiffness: 300 }}
                  />
                )}
                <span className="relative z-10">{c.label}</span>
              </button>
            ))}
          </div>
        </Reveal>

        {loading ? (
          <div className="mt-20 flex justify-center" data-testid="products-loading">
            <Loader2 className="w-8 h-8 text-forest animate-spin" />
          </div>
        ) : filtered.length === 0 ? (
          <div className="mt-20 text-center" data-testid="products-empty">
            <p className="font-serif text-2xl text-ink-soft">Nothing found on this shelf.</p>
            <p className="mt-2 text-sm text-ink-muted">Try a different search — or call us, we probably have it in the store.</p>
          </div>
        ) : (
          <motion.div layout className="mt-10 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6" data-testid="products-grid">
            <AnimatePresence mode="popLayout">
              {filtered.map((p, i) => (
                <ProductCard key={p.id} product={p} index={i} />
              ))}
            </AnimatePresence>
          </motion.div>
        )}

        <p className="mt-8 text-xs text-ink-muted" data-testid="products-count">
          Showing {filtered.length} of {products.length} items · Prices in INR, subject to change in-store.
        </p>
      </section>
    </main>
  );
}
